import { REFETCH_INTERVAL_SLOW } from '../confs/app';
import { useAppSettingStore } from '../store/app-store';
import { useAuth } from './use-auth';
import { scheduleBoundaryRefresh } from '@/client/utils/refresh-jitter';
import { ApiInstance, UserAssetsInfoParams } from '@/libs/sdk/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

// The portfolio is mark-to-market (no event for price moves), the block listener
// writes the projection after the trade receipt, and a stream opened before login
// stays anonymous (no user:asset_changed). So the DPIN build does not rely on SSE
// alone: it polls every 30 s while the tab is visible. Other modes keep 15 s.
export const SIGNED_ASSETS_VISIBLE_POLL_MS = 30_000;
export const signedAssetsRefreshOptions = (mode = import.meta.env.MODE) => ({
  refetchInterval: mode === 'dpin-bsc' ? SIGNED_ASSETS_VISIBLE_POLL_MS : REFETCH_INTERVAL_SLOW,
  refetchIntervalInBackground: false,
});

export const useSignedUserAssets = (api: ApiInstance, params?: UserAssetsInfoParams) => {
  const { data } = useAuth();
  const uid = data?.uid;
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { baseTokenAddress = baseToken.address } = params || {};
  const queryClient = useQueryClient();
  // Content key of the params: a caller passes a new object per render, and the listeners below follow its content.
  const paramsKey = JSON.stringify(params);
  const queryKey = ['SignedUserAssets', api, uid, baseTokenAddress, params];
  const query = useQuery({
    enabled: !!uid,
    staleTime: REFETCH_INTERVAL_SLOW,
    ...signedAssetsRefreshOptions(),
    queryKey,
    queryFn: async () => {
      const { data } = await api.market.getUserAssetsInfo({ ...params, uid: uid!, baseTokenAddress });
      return data;
    },
  });
  useEffect(() => {
    if (!query.isEnabled) return;
    const reftch = () => query.refetch({ cancelRefetch: false });
    // A settlement pushes user:asset_changed to every holder in the same second. The DPIN build spreads those
    // refetches over the client jitter in the first minute after a round boundary, and the header, the trade panel
    // and the user page (one listener each) make one refetch. The refetch reads the query, not this hook: it reaches
    // the hooks that are still mounted when the wait ends, and it sends nothing when none is (logout, route change).
    // A reconnect (base:connected) is not aligned to a round.
    const refreshKey = `SignedUserAssets:${JSON.stringify([uid, baseTokenAddress, paramsKey])}`;
    const refetchActive = () =>
      void queryClient.refetchQueries({ queryKey, exact: true, type: 'active' }, { cancelRefetch: false });
    const onAssetChanged = () => scheduleBoundaryRefresh(refreshKey, refetchActive);
    api.sse.addEventListener('user:asset_changed', onAssetChanged);
    api.sse.addEventListener('base:connected', reftch);
    return () => {
      api.sse.removeEventListener('user:asset_changed', onAssetChanged);
      api.sse.removeEventListener('base:connected', reftch);
    };
  }, [query.isEnabled, api.sse, queryClient, uid, baseTokenAddress, paramsKey]);
  return query;
};
